import { z } from 'zod';
import { CalendarDate, TaskView, WorkflowCategory, WorkflowStatusView } from './project.js';
/**
 * Sprint & board — APM-502.
 *
 * Sprint chỉ có nghĩa với Agile. Project Waterfall dùng cùng bảng `task` nhưng
 * `sprintId` luôn `null`, và board của nó là board không lọc theo sprint.
 */
export const SprintStatus = z.enum(['planning', 'active', 'completed']);
/**
 * Màu nhận dạng của sprint — hex 6 chữ số, không hơn.
 *
 * Hẹp có chủ đích. `task_type.color` nhận mọi cú pháp CSS vì admin gõ tay vào
 * một ô tự do; màu sprint thì người dùng chọn từ một bảng màu có sẵn, nên nới
 * ra chỉ mở đường cho những giá trị mà giao diện phải đoán cách trộn.
 */
export const SprintColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Màu phải ở dạng #rrggbb');
export const CreateSprintInput = z.object({
    name: z.string().trim().min(1).max(120),
    goal: z.string().max(2_000).optional(),
    startDate: CalendarDate,
    endDate: CalendarDate,
    color: SprintColor.optional(),
});
export const UpdateSprintInput = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    goal: z.string().max(2_000).nullable().optional(),
    startDate: CalendarDate.optional(),
    endDate: CalendarDate.optional(),
    /** `null` = bỏ màu, quay về màu tự suy từ `id`. */
    color: SprintColor.nullable().optional(),
});
export const SprintView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    sequence: z.number().int().positive(),
    name: z.string(),
    goal: z.string().nullable(),
    startDate: CalendarDate,
    endDate: CalendarDate,
    status: SprintStatus,
    /** `null` = chưa đặt; giao diện tự suy một màu ổn định từ `id`. */
    color: SprintColor.nullable(),
    startedAt: z.iso.datetime().nullable(),
    completedAt: z.iso.datetime().nullable(),
    taskCount: z.number().int().nonnegative(),
    /** Tổng story point trong sprint — đầu vào của burndown (`APM-802`). */
    totalPoints: z.number().int().nonnegative(),
    donePoints: z.number().int().nonnegative(),
    /** Task (cấp trên cùng) đang ở nhóm trạng thái `done` — cùng mẫu với `taskCount`. */
    doneTaskCount: z.number().int().nonnegative(),
    /**
     * Giờ bấm (`work_session`) trên các task của sprint, kể cả việc con của chúng,
     * 1 chữ số thập phân. Đoạn đang chạy tính tới lúc đọc. Theo sprint **hiện tại**
     * của task: task kéo sang sprint khác mang giờ của nó theo.
     */
    hoursLogged: z.number().nonnegative(),
    /** Số người tham gia: người được giao một task của sprint ∪ người đã bấm giờ trên chúng. */
    memberCount: z.number().int().nonnegative(),
    /**
     * Vị trí nút trên sơ đồ sprint — APM-SPRINT-c.
     *
     * `null` = **tự xếp** theo thời gian. Đó là mặc định, và là trạng thái của
     * mọi sprint cho tới khi ai đó kéo nó. Hai giá trị đi cùng nhau: một cái có
     * mà cái kia `null` là trạng thái không vẽ được, nên chỗ ghi luôn đặt cả hai.
     */
    graphX: z.number().int().nullable(),
    graphY: z.number().int().nullable(),
    /**
     * Đội **nhận** bao nhiêu lúc bắt đầu — APM-AGILE-a. Ảnh chụp, bất biến.
     *
     * `null` khác 0: `null` là "chưa đo" (sprint chưa chạy, hoặc chạy từ trước
     * khi có tính năng này), còn 0 là "bắt đầu sprint mà chưa nhận việc nào". Gộp
     * hai thứ đó lại thì tỉ lệ làm-được/đã-nhận của những sprint cũ hiện ra là
     * vô hạn hoặc bằng 0, và cả hai đều là số bịa.
     */
    committedPoints: z.number().int().nonnegative().nullable(),
    committedTaskCount: z.number().int().nonnegative().nullable(),
    committedAt: z.iso.datetime().nullable(),
});
/** Đóng sprint: task chưa xong đi đâu. */
export const CloseSprintInput = z.object({
    /**
     * `backlog` trả về hàng đợi; có `moveToSprintId` thì chuyển sang sprint đó.
     * Không có lựa chọn "để nguyên": task mồ côi trong một sprint đã đóng sẽ
     * biến mất khỏi mọi board mà vẫn tính vào velocity của sprint cũ.
     */
    carryOver: z.enum(['backlog', 'next_sprint']),
    moveToSprintId: z.uuid().optional(),
});
// ── Board ───────────────────────────────────────────────────────────────────
export const BoardWindowQuery = z.object({
    mode: z.enum(['board', 'list']).default('board'),
    offsets: z
        .preprocess((value) => {
        if (typeof value !== 'string')
            return value;
        try {
            return JSON.parse(value);
        }
        catch {
            return value;
        }
    }, z.record(z.uuid(), z.number().int().min(0)))
        .default({}),
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    sort: z
        .enum([
        'key',
        'position',
        'title',
        'assignee',
        'status',
        'due',
        'timeline',
        'progress',
        'priority',
        'points',
    ])
        .default('key'),
    descending: z.enum(['0', '1']).default('0'),
    text: z.string().max(500).default(''),
    assigneeId: z.union([z.uuid(), z.literal('unassigned')]).optional(),
    taskTypeId: z.uuid().optional(),
    statusId: z.uuid().optional(),
    due: z.enum(['upcoming', 'today', 'overdue']).optional(),
    today: CalendarDate.optional(),
    includeBacklog: z.enum(['0', '1']).default('0'),
});
export const BoardColumn = z.object({
    page: z
        .object({
        offset: z.number().int(),
        limit: z.number().int(),
        total: z.number().int(),
        fullTotal: z.number().int(),
    })
        .optional(),
    status: WorkflowStatusView,
    tasks: z.array(TaskView),
    /**
     * Số **việc** đang ở cột — không tính mốc (`kind = milestone`), vì mốc không
     * phải việc đang làm và không ăn vào trần WIP. `tasks` thì vẫn chứa cả mốc.
     */
    count: z.number().int().nonnegative(),
    /**
     * `true` khi `count > wipLimit`. Server tự tính thay vì để mỗi client tự so
     * — cùng một phép so ở hai nơi là hai cơ hội lệch nhau.
     */
    overWipLimit: z.boolean(),
});
export const BoardView = z.object({
    page: z
        .object({
        offset: z.number().int(),
        limit: z.number().int(),
        total: z.number().int(),
        mode: z.enum(['board', 'list']),
    })
        .optional(),
    filterOptions: z
        .object({
        assignees: z.array(z.object({ id: z.uuid(), name: z.string() })),
        taskTypes: z.array(z.object({ id: z.uuid(), name: z.string(), color: z.string().nullable() })),
    })
        .optional(),
    projectId: z.uuid(),
    /** `null` khi xem board không theo sprint (Waterfall, hoặc "tất cả"). */
    sprint: SprintView.nullable(),
    columns: z.array(BoardColumn),
});
// ── Cột tuỳ biến ────────────────────────────────────────────────────────────
export const CreateStatusInput = z.object({
    name: z.string().trim().min(1).max(60),
    category: WorkflowCategory,
    color: z.string().max(16).optional(),
    wipLimit: z.number().int().min(1).max(999).nullable().optional(),
    /**
     * Cột của riêng một sprint — APM-BOARD-a1.
     *
     * Bỏ trống = cột **ngoài** sprint, hiện ở mọi sprint. Đó là mặc định có chủ
     * đích: một cột người dùng tự thêm ("Chờ duyệt") thường là bước trong quy
     * trình của cả đội, không phải thứ chỉ tồn tại hai tuần.
     */
    sprintId: z.uuid().nullable().optional(),
});
export const UpdateStatusInput = z.object({
    name: z.string().trim().min(1).max(60).optional(),
    category: WorkflowCategory.optional(),
    color: z.string().max(16).nullable().optional(),
    wipLimit: z.number().int().min(1).max(999).nullable().optional(),
    isDefault: z.boolean().optional(),
});
/**
 * Kéo một cột vào khung sprint hoặc ra ngoài khung — APM-BOARD-b.
 *
 * `null` = đưa ra ngoài, tức cột hiện ở **mọi** sprint.
 *
 * ⚠️ Đây là đổi **chủ sở hữu** cột, không phải đổi thứ tự: nó đụng tới mọi task
 * đang nằm trong cột đó. Task nào không thuộc sprint mới của cột sẽ được dời
 * sang cột khác trên board của chính nó — giao diện phải hỏi lại trước khi gọi.
 */
export const MoveStatusInput = z.object({
    sprintId: z.uuid().nullable(),
});
export const ReorderStatusesInput = z.object({
    /** Toàn bộ id cột theo thứ tự mới, trái sang phải. */
    statusIds: z.array(z.uuid()).min(1),
});
// ── Kéo thả ─────────────────────────────────────────────────────────────────
export const MoveTaskInput = z.object({
    /** Bỏ trống = giữ nguyên cột, chỉ đổi thứ tự. */
    statusId: z.uuid().optional(),
    /**
     * Vị trí trong danh sách đích, tính từ 0. Bỏ trống = cuối danh sách.
     * Client gửi **chỉ số**, không gửi mảng thứ tự: gửi cả mảng thì hai người kéo
     * cùng lúc sẽ ghi đè thứ tự của nhau bằng một ảnh chụp đã cũ.
     */
    position: z.number().int().min(0).optional(),
});
export const MoveToSprintInput = z.object({
    /** `null` = trả về backlog. */
    sprintId: z.uuid().nullable(),
    position: z.number().int().min(0).optional(),
});
export const BacklogView = z.object({
    page: BoardColumn.shape.page.optional(),
    projectId: z.uuid(),
    tasks: z.array(TaskView),
    sprints: z.array(SprintView),
});
/**
 * Bốn con số kể lại chuyện một sprint — APM-AGILE-b.
 *
 * ## ⚠️ Đếm theo số TASK, không theo story point
 *
 * `event_log` không ghi point tại từng thời điểm. Đọc point *hiện tại* rồi gán
 * cho quá khứ là để con số tự sửa mình mỗi lần ai đó ước lượng lại — cùng lý do
 * burndown đếm task chứ không đếm point.
 *
 * `committedPoints` là ngoại lệ duy nhất, và nó **được** dùng vì Pha 1 đã chụp
 * ảnh nó lúc bắt đầu.
 */
export const SprintScopeSummary = z.object({
    sprintId: z.uuid(),
    /** Nhận lúc bắt đầu. `null` = sprint chạy trước khi có `APM-AGILE-a`. */
    committedTaskCount: z.number().int().nonnegative().nullable(),
    committedPoints: z.number().int().nonnegative().nullable(),
    /**
     * Task **thêm vào sau khi sprint bắt đầu**, đếm riêng biệt.
     *
     * Dựng từ event `sprint.scope_changed`, nên nó không đổi khi ai đó sửa task
     * về sau. Một task thêm rồi bỏ rồi thêm lại vẫn đếm **một**.
     */
    addedAfterStart: z.number().int().nonnegative(),
    /** Xong tại thời điểm đóng. `null` = sprint chưa đóng. */
    completed: z.number().int().nonnegative().nullable(),
    /** Đẩy sang sau. `null` = chưa đóng — task chưa rời đi đâu cả. */
    carriedOver: z.number().int().nonnegative().nullable(),
});
/**
 * Một cạnh trong đồ thị sprint — APM-SPRINT-a.
 *
 * ⚠️ Quan hệ **lập kế hoạch**: nó nói "sprint này phải xong trước sprint kia".
 * Hai sprint cùng một tiền nhiệm là một *nhánh kế hoạch* — chúng vẫn chạy lần
 * lượt, vì bất biến "một sprint `active` mỗi dự án" giữ nguyên.
 */
export const SprintDependencyView = z.object({
    id: z.uuid(),
    fromSprintId: z.uuid(),
    toSprintId: z.uuid(),
});
export const SprintGraphView = z.object({
    projectId: z.uuid(),
    sprints: z.array(SprintView),
    dependencies: z.array(SprintDependencyView),
});
export const AddSprintDependencyInput = z.object({
    fromSprintId: z.uuid(),
    toSprintId: z.uuid(),
});
/**
 * Đặt vị trí một nút trên sơ đồ — APM-SPRINT-c.
 *
 * ⚠️ Chỉ là **cách sắp xếp cho dễ nhìn**: nó không đổi ngày, không đổi thứ tự
 * chạy, và không xuất hiện trong báo cáo nào. Ràng buộc thứ tự thật nằm ở mũi
 * tên (`SprintDependency`), không ở chỗ nút được đặt.
 */
export const MoveSprintNodeInput = z.object({
    x: z.number().int().min(0).max(20_000),
    y: z.number().int().min(0).max(20_000),
});
//# sourceMappingURL=sprint.js.map